import { ScrollVelocityLockable, ScrollThrottleable } from 'app/contracts/Scroll';

/**
 * @class InteractiveArea
 * Manages if an area on a scrollPlane can be interactive while vertical dragging is active.
 * Doesn't manage any interactive itself. But has public getter interative / callbacks to flag that
 * Also used for touch interfaces to stop upwards scrolling when sideways scrolling type interaction is active
 */
export class InteractiveArea {
	constructor(
		_container: HTMLElement | Element,
		private _scroll: ScrollVelocityLockable,
		private _drag: ScrollThrottleable
	) {}

	private _mouseOver: boolean = false;
	private _interactive: boolean = false;
	private _blocked: boolean = false; //Interaction will be blocked until
	private _onInteractiveStateChange: Function;
	private _onThrottleStateResloved: Function;
	private _velocitylocked: boolean = false;

	public set onInteractiveStateChange(v: Function) {
		this._onInteractiveStateChange = v;
	}

	public set onThrottleStateResloved(v: Function) {
		this._onThrottleStateResloved = v;
	}

	public get interactive(): boolean {
		return this._interactive;
	}

	public get blocked(): boolean {
		return this._blocked;
	}

	/** Mouse over area */
	public mouseOver(): void {
		this._onMouseOver();
	}

	public mouseOut(): void {
		this._onMouseOut();
	}

	/** Need to call this method when interaction starts on module/widget  */
	public interactionStart(): void {
		this._interactionStart();
	}

	/** Need to call this method when interaction stops on module/widget  */
	public interactionEnd(): void {
		this._interactionEnd();
	}

	private _onMouseOver(): void {
		this._mouseOver = true;

		//Check the scroll controller to see if there is too much motion to allow interaction
		//If there is then don't allow any interaction untill
		this._velocitylocked = this._scroll.scrollVelocityLock(() => {
			//If mouse is still over then
			this._velocitylocked = false;
			if (this._mouseOver && !this._interactive) {
				this._setInteractive(true);
			}
		});

		//Page is not moving too fast. So lock throttle vertical scroll/dragging
		if (this._velocitylocked) {
			this._setInteractive(false);
			return;
		}

		//Can interact, so flag and then throttle the drag controller on the scrollplane
		//to limit vertical scroll untill meaningful move up/down is deteched
		this._setInteractive(true);
	}

	private _onMouseOut(): void {
		this._mouseOver = false;
		this._interactive = false;
		this._blocked = false;
		this._drag.resetThrottle();
	}

	/** Interation has started.  */
	private _interactionStart(): void {
		//Throttle the drag action.
		//The callback function is called when a meanful axis movement is deteched:
		//It passes back if the vertical drag
		this._blocked = false;

		this._drag.throttle((verticalDragBlocked: boolean) => {
			//If vertical is not blocked then do not allow interaction on interative area (as it will start to drag up/downwards):
			if (!verticalDragBlocked) {
				this._blocked = true;
				this._setInteractive(false);
			}
			if (this._onThrottleStateResloved) {
				this._onThrottleStateResloved(verticalDragBlocked);
			}
		});
	}

	/** Interation has stopped.  */
	private _interactionEnd(): void {
		this._blocked = false;
		this._drag.resetThrottle();
		if (!this._velocitylocked) {
			this._interactive = true;
		}
	}

	private _setInteractive(interactive: boolean): void {
		this._interactive = interactive;
		if (this._onInteractiveStateChange) {
			this._onInteractiveStateChange(interactive);
		}
	}
}
